import { useCallback } from "react";
import { IconCloseCircleFill } from "@synthesis/shared-icons";
import { downloadExternalRequestFile, downloadInternalRequestFile } from "@synthesis/shared-services";
import { setSnack } from "@synthesis/shared-ui";
import { cn, useDownload } from "@synthesis/shared-utils";

import { AttachmentFileRow, type AttachmentFileMeta } from "../../../../components/AttachmentFileRow";
import { useClientType } from "../../../../shared/hooks/use-client-type";
import type { ApplicationDetailDocument, ApplicationDetailDocumentFile } from "../../application-detail-page.lib";

type AttachedDocumentsContentProps = {
  documents: ApplicationDetailDocument[];
  className?: string;
};

const toAttachmentFileMeta = (file: ApplicationDetailDocumentFile): AttachmentFileMeta => ({
  id: file.id,
  baseName: file.baseName,
  ext: file.ext,
});

export const AttachedDocumentsContent = ({ documents, className }: AttachedDocumentsContentProps) => {
  const clientType = useClientType();
  const downloadFile = useDownload();

  const handleDownload = useCallback(
    async (fileMeta: AttachmentFileMeta) => {
      try {
        const blob = clientType === "internal" ? await downloadInternalRequestFile(fileMeta.id) : await downloadExternalRequestFile(fileMeta.id);
        downloadFile(
          {
            id: fileMeta.id,
            baseName: fileMeta.baseName,
            ...(fileMeta.ext ? { ext: fileMeta.ext } : {}),
          },
          blob,
        );
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setSnack({
          title: "Не удалось скачать файл",
          description: message,
          icon: <IconCloseCircleFill className="size-4" />,
        });
      }
    },
    [clientType, downloadFile],
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {documents.length === 0 ? (
        <p className="paragraph-base text-type-minor py-3">Документы не приложены</p>
      ) : (
        documents.map((document) => (
          <article key={document.id} className="border-neutrals-90 flex flex-col gap-2 border-b px-4 py-3 last:border-b-0">
            <p className="paragraph-base text-type-common">{document.label}</p>
            <ul className="flex list-none flex-col gap-1">
              {document.files.map((file) => (
                <AttachmentFileRow key={file.id} fileMeta={toAttachmentFileMeta(file)} download={handleDownload} />
              ))}
            </ul>
          </article>
        ))
      )}
    </div>
  );
};
